
import React, { useState, useEffect, useRef, FC } from 'react';
import { AssessmentEvent, AssessmentResult, AssessmentSubject, AssessmentStatus, StudentConnection, UserProfile, Organization } from '../types';
import { useTranslate } from '../hooks/useTranslate';
import { Avatar, ProgressRing, AttachmentButton, VoiceInput } from './common';
import { generateContentWithRetry, translateContent } from '../lib/gemini';
import { Type } from '@google/genai';

// --- Helper Components ---

const StatusBadge = ({ status, t }: { status: AssessmentStatus, t: (k: string) => string }) => {
    const colors = {
        'DRAFT': 'bg-gray-100 text-gray-600',
        'SUBMITTED': 'bg-yellow-100 text-yellow-700',
        'NEEDS_REVISION': 'bg-red-100 text-red-700',
        'PUBLISHED': 'bg-green-100 text-green-700'
    };
    return (
        <span className={`px-2 py-1 rounded-full text-xs font-bold ${colors[status] || 'bg-gray-100'}`}>
            {t(`exam_status_${status.toLowerCase()}`)}
        </span>
    );
};

// --- Modals ---

export const CreateAssessmentModal = ({ onClose, onSave, assessmentToEdit, t }: {
    onClose: () => void,
    onSave: (data: Partial<AssessmentEvent>) => void,
    assessmentToEdit?: AssessmentEvent | null,
    t: (key: string) => string
}) => {
    const [title, setTitle] = useState(assessmentToEdit?.title || '');
    const [date, setDate] = useState(assessmentToEdit?.date || new Date().toISOString().split('T')[0]);
    const [type, setType] = useState<AssessmentEvent['type']>(assessmentToEdit?.type || 'exam');
    const [subjects, setSubjects] = useState<AssessmentSubject[]>(assessmentToEdit?.subjects || [{ id: '1', name: 'Math', maxMarks: 100, type: 'marks' }]);

    const handleAddSubject = () => {
        setSubjects([...subjects, { id: Math.random().toString(36).substr(2, 9), name: '', maxMarks: 100, type: 'marks' }]);
    };

    const handleRemoveSubject = (index: number) => {
        setSubjects(subjects.filter((_, i) => i !== index));
    };

    const handleSubjectChange = (index: number, field: keyof AssessmentSubject, value: any) => {
        const newSubjects = [...subjects];
        newSubjects[index] = { ...newSubjects[index], [field]: value };
        setSubjects(newSubjects);
    };

    const handleSave = () => {
        if (!title.trim() || subjects.length === 0) return;
        onSave({ title, date, type, subjects });
        onClose();
    };

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl shadow-xl w-full max-w-lg p-6 max-h-[90vh] flex flex-col">
                <h2 className="text-xl font-bold mb-4">{assessmentToEdit ? t('edit_assessment') : t('create_assessment')}</h2>
                
                <div className="flex-1 overflow-y-auto space-y-4">
                    <input 
                        type="text" 
                        value={title} 
                        onChange={e => setTitle(e.target.value)} 
                        placeholder={t('exam_title')} 
                        className="w-full p-3 border rounded-lg font-bold" 
                    />
                    
                    <div className="flex gap-4">
                        <input 
                            type="date" 
                            value={date} 
                            onChange={e => setDate(e.target.value)} 
                            className="flex-1 p-3 border rounded-lg" 
                        />
                        <select 
                            value={type} 
                            onChange={e => setType(e.target.value as any)} 
                            className="flex-1 p-3 border rounded-lg"
                        >
                            <option value="exam">Exam</option>
                            <option value="test">Unit Test</option>
                            <option value="quiz">Quiz</option>
                            <option value="other">Other</option>
                        </select>
                    </div>

                    <div className="border-t pt-4">
                        <label className="text-xs font-bold text-gray-500 uppercase mb-2 block">{t('subjects_and_marks')}</label>
                        <div className="space-y-2">
                            {subjects.map((subj, idx) => (
                                <div key={subj.id} className="flex gap-2 items-center">
                                    <input 
                                        type="text" 
                                        value={subj.name} 
                                        onChange={e => handleSubjectChange(idx, 'name', e.target.value)} 
                                        placeholder="Subject" 
                                        className="flex-1 p-2 border rounded text-sm" 
                                    />
                                    <select 
                                        value={subj.type} 
                                        onChange={e => handleSubjectChange(idx, 'type', e.target.value)} 
                                        className="w-24 p-2 border rounded text-xs"
                                    >
                                        <option value="marks">{t('type_marks')}</option>
                                        <option value="grade">{t('type_grade')}</option>
                                        <option value="comment">{t('type_comment')}</option>
                                    </select>
                                    {subj.type === 'marks' && (
                                        <input 
                                            type="number" 
                                            value={subj.maxMarks} 
                                            onChange={e => handleSubjectChange(idx, 'maxMarks', parseInt(e.target.value))} 
                                            placeholder="Max" 
                                            className="w-16 p-2 border rounded text-sm text-center" 
                                        />
                                    )}
                                    <button onClick={() => handleRemoveSubject(idx)} className="text-red-400 hover:text-red-600">
                                        <span className="material-icons-round">remove_circle</span>
                                    </button>
                                </div>
                            ))}
                        </div>
                        <button onClick={handleAddSubject} className="mt-2 text-indigo-600 text-sm font-bold flex items-center gap-1">
                            <span className="material-icons-round text-base">add</span> {t('add_subject')}
                        </button>
                    </div>
                </div>

                <div className="flex gap-3 mt-6">
                    <button onClick={onClose} className="flex-1 py-3 bg-gray-100 rounded-lg font-bold">{t('cancel')}</button>
                    <button onClick={handleSave} className="flex-1 py-3 bg-indigo-600 text-white rounded-lg font-bold">{t('save')}</button>
                </div>
            </div>
        </div>
    );
};

// --- Teacher Grading Interface ---

export const AssessmentGradingView = ({ assessment, students, results, onSaveResult, onStatusChange, onClose, t, userRole }: {
    assessment: AssessmentEvent,
    students: StudentConnection[],
    results: AssessmentResult[],
    onSaveResult: (result: Omit<AssessmentResult, 'id'> | AssessmentResult) => Promise<void>,
    onStatusChange: (status: AssessmentStatus, note?: string) => Promise<void>,
    onClose: () => void,
    t: (key: string, reps?: any) => string,
    userRole: 'teacher' | 'principal'
}) => {
    const [localResults, setLocalResults] = useState<Record<string, Partial<AssessmentResult>>>({});
    const [isSaving, setIsSaving] = useState(false);
    const [correctionModalOpen, setCorrectionModalOpen] = useState<{studentId: string, subjectId: string, pendingValue?: string} | null>(null);
    const [unlockedCells, setUnlockedCells] = useState<Set<string>>(new Set());
    const [correctionReason, setCorrectionReason] = useState('');
    const [isAIProcessing, setIsAIProcessing] = useState(false);

    // Initialize local state from props
    useEffect(() => {
        const initial: Record<string, Partial<AssessmentResult>> = {};
        students.forEach(s => {
            const existing = results.find(r => r.studentId === s.id);
            if (existing) {
                initial[s.id] = { ...existing };
            } else {
                initial[s.id] = { 
                    assessmentId: assessment.id, 
                    studentId: s.id, 
                    studentName: s.childName, 
                    scores: {}, 
                    isWithheld: false 
                };
            }
        });
        setLocalResults(initial);
    }, [results, students, assessment.id]);

    const handleScoreChange = (studentId: string, subjectId: string, value: string) => {
        // If published, don't allow direct edit without unlock flow (implemented via correction modal trigger)
        const cellKey = `${studentId}_${subjectId}`;
        
        if (assessment.status === 'PUBLISHED' && userRole === 'teacher' && !unlockedCells.has(cellKey)) {
            setCorrectionModalOpen({ studentId, subjectId, pendingValue: value });
            return;
        }

        setLocalResults(prev => ({
            ...prev,
            [studentId]: {
                ...prev[studentId],
                scores: {
                    ...prev[studentId]?.scores,
                    [subjectId]: { value, remarks: prev[studentId]?.scores?.[subjectId]?.remarks }
                }
            }
        }));
    };

    const handleWithholdToggle = (studentId: string) => {
        setLocalResults(prev => ({
            ...prev,
            [studentId]: {
                ...prev[studentId],
                isWithheld: !prev[studentId]?.isWithheld
            }
        }));
    };

    const handleSaveAll = async () => {
        setIsSaving(true);
        try {
            const promises = Object.values(localResults).map((res: Partial<AssessmentResult>) => {
                if (res.studentId) return onSaveResult(res as AssessmentResult);
                return Promise.resolve();
            });
            await Promise.all(promises);
            // alert('Saved successfully');
        } catch (e) {
            console.error(e);
            alert('Error saving results');
        } finally {
            setIsSaving(false);
        }
    };

    const handleSubmit = async () => {
        await handleSaveAll();
        if (window.confirm("Submit grades for Principal approval? You won't be able to edit until approved or rejected.")) {
            await onStatusChange('SUBMITTED');
            onClose();
        }
    };

    const handleConfirmCorrection = () => {
        if (!correctionModalOpen) return;
        const { studentId, subjectId, pendingValue } = correctionModalOpen;
        const cellKey = `${studentId}_${subjectId}`;
        
        // Unlock this cell for editing
        setUnlockedCells(prev => new Set(prev).add(cellKey));
        
        // Apply the pending value that triggered this modal
        if (pendingValue !== undefined) {
             setLocalResults(prev => ({
                ...prev,
                [studentId]: {
                    ...prev[studentId],
                    scores: {
                        ...prev[studentId]?.scores,
                        [subjectId]: { value: pendingValue, remarks: prev[studentId]?.scores?.[subjectId]?.remarks }
                    },
                    // Add correction log logic here in future if needed
                }
            }));
        }
        
        setCorrectionModalOpen(null);
        setCorrectionReason('');
        // alert("Correction logged. You can now edit the cell.");
    };

    // --- AI Grading Handlers ---

    const processAIGrading = async (textInput: string | undefined, imageInput: string | undefined) => {
        if (!textInput && !imageInput) return;
        
        setIsAIProcessing(true);
        try {
            const studentList = students.map(s => s.childName).join(', ');
            const subjectList = assessment.subjects.map(s => s.name).join(', ');
            
            const prompt = `
            You are a data entry assistant for a teacher.
            
            Context:
            - Class Students: [${studentList}]
            - Exam Subjects: [${subjectList}]
            
            Task:
            Extract student grades from the input. 
            
            Rules:
            1. Match the input names to the "Class Students" list using fuzzy matching. If a name is very similar, use the exact name from the list.
            2. Match the input subjects to the "Exam Subjects" list.
            3. Return a JSON array of objects. Each object should have:
               - "studentName": The exact matched name from the class list.
               - "scores": An array of objects, each containing "subject" (the exact Subject Name) and "score" (the extracted value).
            
            Input Data:
            ${textInput || "See image."}
            `;

            const parts: any[] = [{ text: prompt }];
            if (imageInput) {
                // If data URL, split to get base64
                const base64Data = imageInput.split(',')[1] || imageInput;
                parts.unshift({
                    inlineData: {
                        mimeType: 'image/jpeg', // Assuming jpeg/png
                        data: base64Data
                    }
                });
            }

            const response = await generateContentWithRetry({
                model: 'gemini-3-flash-preview',
                contents: { parts },
                config: {
                    responseMimeType: 'application/json',
                    responseSchema: {
                        type: Type.ARRAY,
                        items: {
                            type: Type.OBJECT,
                            properties: {
                                studentName: { type: Type.STRING },
                                scores: {
                                    type: Type.ARRAY,
                                    items: {
                                        type: Type.OBJECT,
                                        properties: {
                                            subject: { type: Type.STRING },
                                            score: { type: Type.STRING }
                                        },
                                        required: ['subject', 'score']
                                    }
                                }
                            },
                            required: ['studentName', 'scores']
                        }
                    }
                }
            });

            const data = JSON.parse(response.text || '[]');
            
            if (Array.isArray(data) && data.length > 0) {
                setLocalResults(prev => {
                    const next = { ...prev };
                    data.forEach((item: any) => {
                        // Find student ID by name
                        const student = students.find(s => s.childName.toLowerCase() === item.studentName?.toLowerCase());
                        if (student) {
                            const existing = next[student.id] || { 
                                assessmentId: assessment.id, 
                                studentId: student.id, 
                                studentName: student.childName, 
                                scores: {}, 
                                isWithheld: false 
                            };
                            
                            // Merge scores
                            if (item.scores && Array.isArray(item.scores)) {
                                item.scores.forEach((scoreItem: any) => {
                                    const subjectName = scoreItem.subject;
                                    const scoreValue = scoreItem.score;
                                    const subject = assessment.subjects.find(s => s.name.toLowerCase() === subjectName?.toLowerCase());
                                    if (subject) {
                                        existing.scores = {
                                            ...existing.scores,
                                            [subject.id]: { value: scoreValue, remarks: existing.scores?.[subject.id]?.remarks }
                                        };
                                    }
                                });
                            }
                            next[student.id] = existing;
                        }
                    });
                    return next;
                });
                alert(`AI processed ${data.length} students. Please review the grades.`);
            } else {
                alert("AI couldn't find any matching grades. Please check the input.");
            }

        } catch (e) {
            console.error("AI Grading Error", e);
            alert("Failed to process grades. Please try again.");
        } finally {
            setIsAIProcessing(false);
        }
    };

    const handleImageScan = (dataUrl: string) => {
        processAIGrading(undefined, dataUrl);
    };

    const handleVoiceEntry = (transcript: string) => {
        processAIGrading(transcript, undefined);
    };

    // Principal controls
    const [rejectNote, setRejectNote] = useState('');
    const [showRejectModal, setShowRejectModal] = useState(false);

    const isReadOnly = userRole === 'teacher' && (assessment.status === 'SUBMITTED' || assessment.status === 'PUBLISHED');

    return (
        <div className="fixed inset-0 bg-white z-50 flex flex-col">
            <div className="p-4 border-b flex flex-wrap justify-between items-center shadow-sm gap-4">
                <div>
                    <h2 className="text-xl font-bold flex items-center gap-2">
                        {assessment.title}
                        <StatusBadge status={assessment.status} t={t} />
                    </h2>
                    <p className="text-sm text-gray-500">{new Date(assessment.date).toLocaleDateString()} • {assessment.subjects.length} Subjects</p>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                    
                    {!isReadOnly && userRole === 'teacher' && (
                        <div className="flex items-center gap-2 mr-4 bg-gray-50 p-1 rounded-lg border">
                            <div className="relative">
                                <AttachmentButton onImageSelect={handleImageScan} />
                            </div>
                            <div className="h-6 w-px bg-gray-300 mx-1"></div>
                            <VoiceInput 
                                onTranscriptReceived={handleVoiceEntry} 
                                onProcessingChange={(processing) => {
                                    // VoiceInput handles its own recording state, but we can sync the AI processing state if needed
                                    // Here we primarily use the transcript callback.
                                    if(processing) setIsAIProcessing(true); // crude sync
                                }}
                                className=""
                            />
                            {isAIProcessing && <span className="text-xs text-indigo-600 font-bold animate-pulse px-2">AI Processing...</span>}
                        </div>
                    )}

                    <button onClick={onClose} className="px-4 py-2 text-gray-600 font-bold hover:bg-gray-100 rounded-lg">{t('cancel')}</button>
                    {!isReadOnly && <button onClick={handleSaveAll} disabled={isSaving} className="px-4 py-2 bg-indigo-100 text-indigo-700 font-bold rounded-lg hover:bg-indigo-200">{isSaving ? 'Saving...' : t('save_draft')}</button>}
                    
                    {userRole === 'teacher' && assessment.status === 'DRAFT' && (
                        <button onClick={handleSubmit} className="px-4 py-2 bg-green-600 text-white font-bold rounded-lg hover:bg-green-700">{t('submit_for_approval')}</button>
                    )}
                    {userRole === 'teacher' && assessment.status === 'NEEDS_REVISION' && (
                        <button onClick={handleSubmit} className="px-4 py-2 bg-green-600 text-white font-bold rounded-lg hover:bg-green-700">Resubmit</button>
                    )}

                    {userRole === 'principal' && assessment.status === 'SUBMITTED' && (
                        <>
                            <button onClick={() => setShowRejectModal(true)} className="px-4 py-2 bg-red-100 text-red-700 font-bold rounded-lg hover:bg-red-200">{t('request_changes')}</button>
                            <button onClick={() => { if(window.confirm(t('confirm_publish', { count: students.length.toString(), withheldCount: Object.values(localResults).filter((r: Partial<AssessmentResult>) => r.isWithheld).length.toString() }))) onStatusChange('PUBLISHED'); }} className="px-4 py-2 bg-green-600 text-white font-bold rounded-lg hover:bg-green-700">{t('approve_and_publish')}</button>
                        </>
                    )}
                </div>
            </div>

            {assessment.principalNote && (
                <div className="bg-yellow-50 p-4 border-b border-yellow-200 text-yellow-800">
                    <p className="font-bold text-sm">{t('principal_note')}:</p>
                    <p className="text-sm">{assessment.principalNote}</p>
                </div>
            )}

            <div className="flex-1 overflow-auto p-4">
                <table className="w-full border-collapse min-w-[800px]">
                    <thead>
                        <tr className="bg-gray-50 border-b">
                            <th className="p-3 text-left font-bold text-gray-600 sticky left-0 bg-gray-50 z-10 w-48">Student</th>
                            {assessment.subjects.map(subj => (
                                <th key={subj.id} className="p-3 text-center font-bold text-gray-600 w-32">
                                    {subj.name}
                                    <span className="block text-[10px] font-normal text-gray-400">
                                        {subj.type === 'marks' ? `Max: ${subj.maxMarks}` : subj.type}
                                    </span>
                                </th>
                            ))}
                            <th className="p-3 text-center font-bold text-gray-600 w-24">{t('withheld')}?</th>
                        </tr>
                    </thead>
                    <tbody>
                        {students.map(student => {
                            const result = localResults[student.id] || {};
                            return (
                                <tr key={student.id} className="border-b hover:bg-gray-50 group">
                                    <td className="p-3 sticky left-0 bg-white group-hover:bg-gray-50 border-r flex items-center gap-3">
                                        <Avatar name={student.childName} photoUrl={student.photoUrl} size="sm" />
                                        <span className="font-medium text-sm truncate">{student.childName}</span>
                                    </td>
                                    {assessment.subjects.map(subj => (
                                        <td key={subj.id} className="p-2 border-r text-center">
                                            <input
                                                type={subj.type === 'marks' ? 'number' : 'text'}
                                                value={result.scores?.[subj.id]?.value || ''}
                                                onChange={e => handleScoreChange(student.id, subj.id, e.target.value)}
                                                disabled={isReadOnly && assessment.status !== 'PUBLISHED'} // Allow edit via correction flow if published
                                                className={`w-full p-2 text-center rounded border focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none ${
                                                    subj.type === 'marks' && Number(result.scores?.[subj.id]?.value) > subj.maxMarks ? 'bg-red-50 text-red-600 font-bold' : ''
                                                } disabled:bg-gray-100 disabled:text-gray-500`}
                                                placeholder={subj.type === 'marks' ? '-' : '...'}
                                            />
                                        </td>
                                    ))}
                                    <td className="p-2 text-center">
                                        <button 
                                            onClick={() => handleWithholdToggle(student.id)}
                                            className={`p-2 rounded-full transition-colors ${result.isWithheld ? 'bg-red-100 text-red-600' : 'text-gray-300 hover:bg-gray-100'}`}
                                            title={t('withhold_result')}
                                        >
                                            <span className="material-icons-round">{result.isWithheld ? 'visibility_off' : 'visibility'}</span>
                                        </button>
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>

            {/* Correction Modal */}
            {correctionModalOpen && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white p-6 rounded-xl w-96">
                        <h3 className="font-bold text-lg mb-2">{t('correction_reason')}</h3>
                        <p className="text-sm text-gray-500 mb-4">You are modifying a published result. This action will be logged.</p>
                        <input 
                            type="text" 
                            className="w-full border p-2 rounded mb-4" 
                            placeholder="e.g., Data entry error"
                            value={correctionReason}
                            onChange={e => setCorrectionReason(e.target.value)}
                        />
                        <div className="flex gap-2 justify-end">
                            <button onClick={() => setCorrectionModalOpen(null)} className="px-4 py-2 bg-gray-100 rounded">{t('cancel')}</button>
                            <button onClick={handleConfirmCorrection} className="px-4 py-2 bg-indigo-600 text-white rounded">Confirm</button>
                        </div>
                    </div>
                </div>
            )}

            {/* Reject Modal */}
            {showRejectModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white p-6 rounded-xl w-96">
                        <h3 className="font-bold text-lg mb-2">Request Changes</h3>
                        <textarea 
                            className="w-full border p-2 rounded mb-4 h-32" 
                            placeholder="Note for the teacher..."
                            value={rejectNote}
                            onChange={e => setRejectNote(e.target.value)}
                        />
                        <div className="flex gap-2 justify-end">
                            <button onClick={() => setShowRejectModal(false)} className="px-4 py-2 bg-gray-100 rounded">{t('cancel')}</button>
                            <button onClick={async () => {
                                await onStatusChange('NEEDS_REVISION', rejectNote);
                                setShowRejectModal(false);
                                onClose();
                            }} className="px-4 py-2 bg-red-600 text-white rounded">Send Back</button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

const RemarkItem: FC<{ remarks: string, userLanguage?: string, t: (k: string) => string }> = ({ remarks, userLanguage = 'en', t }) => {
    const [translated, setTranslated] = useState<string | null>(null);
    const [showOriginal, setShowOriginal] = useState(false);
    const [isTranslating, setIsTranslating] = useState(false);

    useEffect(() => {
        setTranslated(null);
        setShowOriginal(false);
    }, [userLanguage, remarks]);

    const handleTranslate = async () => {
        if (!translated) {
            setIsTranslating(true);
            try {
                const res = await translateContent(remarks, userLanguage);
                setTranslated(res);
                setShowOriginal(false);
            } catch (err) {
                console.error("Remark translation failed", err);
            } finally {
                setIsTranslating(false);
            }
        } else {
            setShowOriginal(!showOriginal);
        }
    };

    const textToShow = showOriginal ? remarks : (translated || remarks);

    return (
        <div className="mt-1">
            <p className="text-xs text-gray-500 italic">"{textToShow}"</p>
            <button 
                onClick={handleTranslate}
                disabled={isTranslating}
                className="text-[11px] font-bold text-indigo-600 hover:text-indigo-800 flex items-center gap-0.5 mt-0.5"
            >
                <span className="material-icons-round text-[12px]">translate</span>
                {isTranslating ? (t('processing_translation') || 'Translating...') : (showOriginal ? (t('see_translation') || 'See Translation') : (translated ? (t('show_original') || 'Show Original') : (t('translate') || 'Translate')))}
            </button>
        </div>
    );
};

// --- Parent View ---

export const ParentAssessmentList = ({ events, results, t, org, userLanguage = 'en' }: { events: AssessmentEvent[], results: AssessmentResult[], t: (key: string) => string, org?: Organization, userLanguage?: string }) => {
    const publishedEvents = events.filter(e => e.status === 'PUBLISHED');
    const [selectedEventId, setSelectedEventId] = useState<string | null>(null);

    const selectedEvent = publishedEvents.find(e => e.id === selectedEventId);
    const selectedResult = selectedEvent ? results.find(r => r.assessmentId === selectedEvent.id) : null;

    if (selectedEvent && selectedResult) {
        if (selectedResult.isWithheld) {
            return (
                <div className="p-4 flex flex-col items-center justify-center h-64 text-center">
                    <button onClick={() => setSelectedEventId(null)} className="self-start mb-4 text-gray-500 flex items-center gap-1"><span className="material-icons-round">arrow_back</span> Back</button>
                    <span className="material-icons-round text-5xl text-red-300 mb-4">lock</span>
                    <h3 className="text-xl font-bold text-gray-800 mb-2">{t('result_withheld_msg')}</h3>
                    <p className="text-gray-500 text-sm">Please contact the school administration.</p>
                </div>
            );
        }

        // Calculate total/percentage for "Marks" type subjects
        let totalMax = 0;
        let totalScore = 0;
        let hasMarks = false;

        selectedEvent.subjects.forEach(subj => {
            if (subj.type === 'marks') {
                hasMarks = true;
                totalMax += subj.maxMarks;
                totalScore += Number(selectedResult.scores[subj.id]?.value || 0);
            }
        });

        const percentage = totalMax > 0 ? (totalScore / totalMax) * 100 : 0;

        return (
            <div className="p-4 space-y-4 animate-slide-up">
                <button onClick={() => setSelectedEventId(null)} className="text-gray-500 flex items-center gap-1 text-sm font-bold mb-2"><span className="material-icons-round text-base">arrow_back</span> Back to List</button>
                
                <div className="bg-white rounded-xl shadow-sm border p-6 text-center">
                    <h2 className="text-2xl font-bold text-indigo-900">{selectedEvent.title}</h2>
                    <p className="text-sm text-gray-500 mb-6">{new Date(selectedEvent.date).toLocaleDateString()}</p>
                    
                    {hasMarks && (
                        <div className="flex justify-center items-center gap-8 mb-6">
                            <div className="w-32 h-32">
                                <ProgressRing percentage={percentage} />
                            </div>
                            <div className="text-left">
                                <p className="text-xs font-bold text-gray-400 uppercase tracking-wider">{t('total_score')}</p>
                                <p className="text-3xl font-bold text-gray-800">{totalScore} <span className="text-lg text-gray-400">/ {totalMax}</span></p>
                                <p className="text-sm font-medium text-indigo-600 mt-1">{percentage.toFixed(1)}%</p>
                            </div>
                        </div>
                    )}
                </div>

                <div className="bg-white rounded-xl shadow-sm border overflow-hidden">
                    <table className="w-full">
                        <thead className="bg-gray-50 border-b">
                            <tr>
                                <th className="p-3 text-left text-xs font-bold text-gray-500 uppercase">{t('subject')}</th>
                                <th className="p-3 text-center text-xs font-bold text-gray-500 uppercase">{t('score')} / {t('grade')}</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100">
                            {selectedEvent.subjects.map(subj => {
                                const score = selectedResult.scores[subj.id];
                                return (
                                    <tr key={subj.id}>
                                        <td className="p-4">
                                            <p className="font-bold text-gray-800">{subj.name}</p>
                                            {score?.remarks && <RemarkItem remarks={score.remarks} userLanguage={userLanguage} t={t} />}
                                        </td>
                                        <td className="p-4 text-center font-medium text-gray-700">
                                            {score?.value || '-'}
                                            {subj.type === 'marks' && <span className="text-xs text-gray-400 ml-1">/ {subj.maxMarks}</span>}
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>

                <div className="p-4 bg-gray-100 rounded-xl text-center text-xs text-gray-500 flex items-center justify-center gap-2">
                    <span className="material-icons-round text-base">info</span>
                    {t('official_record_note')}
                </div>
            </div>
        );
    }

    return (
        <div className="p-4 space-y-4">
            <h2 className="text-xl font-bold text-gray-800 mb-2">{t('exam_results')}</h2>
            {publishedEvents.length === 0 && <p className="text-gray-500 text-center py-8">No results published yet.</p>}
            
            {publishedEvents.map(event => {
                const result = results.find(r => r.assessmentId === event.id);
                // If withholding is active, we still show the card but with a lock icon, or hide scores.
                // Spec said "Result Withheld" text.
                
                return (
                    <div key={event.id} onClick={() => setSelectedEventId(event.id)} className="bg-white p-4 rounded-xl shadow-sm border flex justify-between items-center cursor-pointer hover:border-indigo-300 transition-colors">
                        <div>
                            <h3 className="font-bold text-lg text-indigo-900">{event.title}</h3>
                            <p className="text-sm text-gray-500">{new Date(event.date).toLocaleDateString()}</p>
                        </div>
                        {result?.isWithheld ? (
                            <span className="bg-red-100 text-red-600 p-2 rounded-full"><span className="material-icons-round">lock</span></span>
                        ) : (
                            <span className="material-icons-round text-gray-400">chevron_right</span>
                        )}
                    </div>
                );
            })}
        </div>
    );
};

// --- Printable View (For Principal) ---

export const BatchPrintView = ({ assessment, students, results, org }: { assessment: AssessmentEvent, students: StudentConnection[], results: AssessmentResult[], org: Organization }) => {
    return (
        <div className="printable w-full bg-gray-100 print:bg-white">
            {students.map((student, index) => {
                const result = results.find(r => r.studentId === student.id);
                if (!result || result.isWithheld) return null; // Don't print withheld

                return (
                    <div key={student.id} className="w-full max-w-[210mm] mx-auto bg-white p-12 mb-8 shadow-lg print:shadow-none print:mb-0 print:w-full print:max-w-none print:h-screen break-after-page flex flex-col" style={{ pageBreakAfter: 'always' }}>
                        {/* Header */}
                        <div className="flex items-center gap-4 mb-8 border-b-2 border-gray-800 pb-4">
                            {org.photoUrl && <img src={org.photoUrl} className="h-20 w-20 object-contain" alt="Logo" />}
                            <div className="flex-1 text-center">
                                <h1 className="text-3xl font-bold uppercase tracking-wider">{org.name}</h1>
                                <p className="text-sm text-gray-600">{org.address.street}, {org.address.city}</p>
                            </div>
                        </div>

                        {/* Student Info */}
                        <div className="mb-8">
                            <h2 className="text-xl font-bold text-center mb-6 uppercase border bg-gray-100 p-2 print:bg-gray-100 print:border-gray-800">Report Card: {assessment.title}</h2>
                            <div className="grid grid-cols-2 gap-4 text-sm">
                                <p><span className="font-bold">Name:</span> {student.childName}</p>
                                <p><span className="font-bold">Date:</span> {new Date(assessment.date).toLocaleDateString()}</p>
                                {/* Add Class/Roll if available */}
                            </div>
                        </div>

                        {/* Marks Table */}
                        <table className="w-full border-collapse border border-gray-800 mb-8">
                            <thead>
                                <tr className="bg-gray-100 print:bg-gray-100">
                                    <th className="border border-gray-800 p-3 text-left">Subject</th>
                                    <th className="border border-gray-800 p-3 text-center">Max Marks</th>
                                    <th className="border border-gray-800 p-3 text-center">Obtained</th>
                                    <th className="border border-gray-800 p-3 text-left">Remarks</th>
                                </tr>
                            </thead>
                            <tbody>
                                {assessment.subjects.map(subj => {
                                    const score = result.scores[subj.id];
                                    return (
                                        <tr key={subj.id}>
                                            <td className="border border-gray-800 p-3 font-bold">{subj.name}</td>
                                            <td className="border border-gray-800 p-3 text-center">{subj.type === 'marks' ? subj.maxMarks : '-'}</td>
                                            <td className="border border-gray-800 p-3 text-center font-bold">
                                                {score?.value || '-'}
                                            </td>
                                            <td className="border border-gray-800 p-3 text-sm italic">{score?.remarks || ''}</td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>

                        {/* Footer Signatures */}
                        <div className="mt-auto flex justify-between pt-16">
                            <div className="text-center w-48 border-t border-gray-800 pt-2">
                                <p className="font-bold">Class Teacher</p>
                            </div>
                            <div className="text-center w-48 border-t border-gray-800 pt-2">
                                <p className="font-bold">Principal</p>
                            </div>
                        </div>
                        <p className="text-center text-xs text-gray-400 mt-8">Generated by EduGravity</p>
                    </div>
                );
            })}
        </div>
    );
};

// --- Assessment Manager (Wrapper for Teacher) ---

export const AssessmentManager = ({ 
    classId, academicYearId, user, events, results, students, 
    onSaveEvent, onSaveResult, onStatusChange, t 
}: { 
    classId: string, 
    academicYearId: string, 
    user: UserProfile, 
    events: AssessmentEvent[], 
    results: AssessmentResult[], 
    students: StudentConnection[],
    onSaveEvent: (e: any) => Promise<void>,
    onSaveResult: (r: any) => Promise<void>,
    onStatusChange: (eventId: string, status: AssessmentStatus, note?: string) => Promise<void>,
    t: (key: string) => string
}) => {
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [selectedEvent, setSelectedEvent] = useState<AssessmentEvent | null>(null);

    return (
        <div className="p-4 space-y-4">
            <button 
                onClick={() => setShowCreateModal(true)} 
                className="w-full bg-indigo-600 text-white py-3 rounded-xl font-bold shadow-lg flex items-center justify-center gap-2"
            >
                <span className="material-icons-round">add_task</span>
                {t('create_assessment')}
            </button>

            {events.length === 0 && <p className="text-center text-gray-500 py-8">No exams created yet.</p>}

            <div className="grid gap-3">
                {events.map(event => (
                    <div key={event.id} className="bg-white p-4 rounded-xl shadow-sm border flex justify-between items-center group">
                        <div onClick={() => setSelectedEvent(event)} className="flex-1 cursor-pointer">
                            <h3 className="font-bold text-gray-800 group-hover:text-indigo-600">{event.title}</h3>
                            <div className="flex items-center gap-2 text-sm text-gray-500 mt-1">
                                <span>{new Date(event.date).toLocaleDateString()}</span>
                                <span className="text-gray-300">•</span>
                                <StatusBadge status={event.status} t={t} />
                            </div>
                        </div>
                        <button onClick={() => setSelectedEvent(event)} className="p-2 text-gray-400 hover:bg-gray-100 rounded-full">
                            <span className="material-icons-round">edit</span>
                        </button>
                    </div>
                ))}
            </div>

            {showCreateModal && (
                <CreateAssessmentModal 
                    onClose={() => setShowCreateModal(false)}
                    onSave={async (data) => {
                        await onSaveEvent({
                            ...data,
                            classId,
                            academicYearId,
                            status: 'DRAFT',
                            createdBy: user.id,
                            createdAt: new Date()
                        });
                    }}
                    t={t}
                />
            )}

            {selectedEvent && (
                <AssessmentGradingView 
                    assessment={selectedEvent}
                    students={students.filter(s => s.approvalStatus === 'approved')}
                    results={results.filter(r => r.assessmentId === selectedEvent.id)}
                    onSaveResult={onSaveResult}
                    onStatusChange={(status, note) => onStatusChange(selectedEvent.id, status, note)}
                    onClose={() => setSelectedEvent(null)}
                    t={t}
                    userRole="teacher"
                />
            )}
        </div>
    );
};
